/**
 * React app entry point. Renders the TanStack Router into <div id="root" />.
 * Included from src/index.html.
 */

import { Toaster } from "@d12labs/ui/sonner";
import { MutationCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
// Importing the stylesheet here is what the @d12labs/tailwind plugin hooks into to
// compile Tailwind and bundle the result. The HTML entry only loads this
// file, so this is the single point where CSS enters the build graph.
import "./index.css";
import { handleMutationError } from "./lib/mutation-errors";
import { ThemeProvider } from "./lib/theme-context";
import { router } from "./router";

const queryClient = new QueryClient({
	// Failed mutations toast by default; see src/lib/mutation-errors.ts for opt-outs.
	mutationCache: new MutationCache({ onError: handleMutationError }),
	defaultOptions: {
		queries: { refetchOnWindowFocus: false, retry: 1 },
	},
});

const elem = document.getElementById("root");
if (!elem) {
	throw new Error("#root element not found in index.html");
}

const app = (
	<StrictMode>
		<ThemeProvider>
			<QueryClientProvider client={queryClient}>
				<RouterProvider router={router} />
				<Toaster position="top-right" richColors closeButton />
			</QueryClientProvider>
		</ThemeProvider>
	</StrictMode>
);

if (import.meta.hot) {
	import.meta.hot.data.root ??= createRoot(elem);
	import.meta.hot.data.root.render(app);
} else {
	createRoot(elem).render(app);
}
